import { Form, Space } from 'antd';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { ChannelFormProps } from './ChannelForm.types';
import { StyledModal, StyledSteps } from './ChannelForm.styles';
import { STEPS } from './ChannelForm.constants';
import { Footer } from './components';
import { Title } from '@/components';
import { NAMESPACE } from '@/constants';
import { useChannelForm } from './hooks/useChannelForm';
import { ChannelFormContext } from './ChannelForm.context';
import { useConfirmModal } from '@/hooks/useConfirmModal';
import { getLineItemFormMode, type LineItemFormMode } from '../../utils/lineItemFormMode';

const ChannelForm: FC<
  Pick<
    ChannelFormProps,
    'mediaPlanId' | 'totalBudget' | 'planCurrency' | 'planStartDate' | 'planEndDate' | 'close' | 'channel'
  >
> = ({ mediaPlanId, totalBudget, planCurrency, planStartDate, planEndDate, channel, close }) => {
  const {
    currentStep,
    form,
    context,
    submit,
    handleValuesChange,
    handleNext,
    handlePrev,
    initialValues,
    isFormValid,
    isFormTouched,
    isSubmitting
  } = useChannelForm({ mediaPlanId, totalBudget, planCurrency, planStartDate, planEndDate, close, channel });

  return (
    <Form
      form={form}
      initialValues={initialValues}
      name="lineItemWizard"
      onValuesChange={handleValuesChange}
      layout="vertical"
    >
      <ChannelFormContext.Provider value={context}>
        <Space direction="vertical">
          <StyledSteps current={currentStep} items={STEPS} />
          {STEPS.map((step, index) => (
            <div key={step.name} style={{ display: currentStep === index ? 'block' : 'none' }}>
              {step.content}
            </div>
          ))}
        </Space>
        <Footer
          step={currentStep}
          handleNext={handleNext}
          handlePrev={handlePrev}
          isFormValid={isFormValid}
          isSubmitting={isSubmitting}
          isFormTouched={isFormTouched}
          submit={submit}
          stepsCount={STEPS.length - 1}
        />
      </ChannelFormContext.Provider>
    </Form>
  );
};

/** Modal title per mode. */
const TITLE_KEY: Record<LineItemFormMode, string> = {
  create: 'addLineItem',
  edit: 'editLineItem',
  duplicate: 'duplicateLineItem'
};

/** Cancel-confirmation copy per mode — a duplicate is discarded like a new line, not an edit. */
const CANCEL_CONTENT_KEY: Record<LineItemFormMode, string> = {
  create: 'cancelLineItemCreationProcess',
  edit: 'cancelLineItemEditingProcess',
  duplicate: 'cancelLineItemCreationProcess'
};

export default ({ close, isOpen, channel, ...rest }: ChannelFormProps) => {
  const { t } = useTranslation(NAMESPACE.MEDIA_PLAN_PAGE);
  const mode = getLineItemFormMode(channel);
  const title = t(TITLE_KEY[mode]);
  const modalContent = t(CANCEL_CONTENT_KEY[mode]);
  const { handleConfirm } = useConfirmModal({
    onCancel: close,
    title: t('cancelModalTitle'),
    content: modalContent,
    okText: t('cancel', { ns: NAMESPACE.COMMON }),
    cancelText: t('close', { ns: NAMESPACE.COMMON })
  });

  return (
    <StyledModal
      footer={false}
      destroyOnHidden
      title={<Title level={4}>{title}</Title>}
      open={isOpen}
      width={1328}
      onCancel={handleConfirm}
    >
      <ChannelForm close={close} channel={channel} {...rest} />
    </StyledModal>
  );
};
